import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trophy, Users, Clock, DollarSign, Swords, Lock } from 'lucide-react';
import { motion } from 'framer-motion';
import { StageEmptyState } from '../components/StageEmptyState';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';



export default function BattlesPage() {
  const { user, isAuthenticated } = useAuth();
  const [battles, setBattles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ mode: '', status: '' });
  const [hasAccess, setHasAccess] = useState(false);

  // Check if user has battle access
  useEffect(() => {
    if (user) {
      // Only users with explicit canAccessBattles permission or owners can access
      setHasAccess(user.can_access_battles === true || user.role === 'owner');
    } else {
      setHasAccess(false);
    }
  }, [user]);

  useEffect(() => {
    if (hasAccess) {
      fetchBattles();
    } else {
      setLoading(false);
    }
  }, [filters, hasAccess]);

  const fetchBattles = async () => {
    setLoading(true);
    try {
      let url = `${API}/battles?limit=50`;
      if (filters.mode && filters.mode !== 'all') url += `&battle_mode=${filters.mode}`;
      if (filters.status && filters.status !== 'all') url += `&status=${filters.status}`;
      
      const response = await fetch(url);
      if (response.ok) setBattles(await response.json());
    } catch (error) {
      console.error('Failed to fetch battles:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'open': 'bg-green-500',
      'in_progress': 'bg-secondary animate-pulse-glow',
      'voting': 'bg-primary',
      'completed': 'bg-muted-foreground'
    };
    return colors[status] || 'bg-muted';
  };

  // Show locked state if user doesn't have access
  if (!hasAccess) {
    return (
      <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="battles-page-locked">
        <div className="max-w-lg mx-auto text-center py-20">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-muted/30 flex items-center justify-center">
            <Lock className="w-10 h-10 text-muted-foreground" />
          </div>
          <h1 className="font-heading text-3xl font-bold mb-4">Battles Access Required</h1>
          <p className="text-muted-foreground mb-6">
            The Cypher Battles feature is currently restricted. 
            Only authorized users can access battles and cypher competitions.
          </p>
          <p className="text-sm text-muted-foreground">
            Contact DMV Soundstage admin to request access.
          </p>
          <div className="mt-8">
            <Link to="/">
              <Button variant="outline">Back to Home</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="battles-page">
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase mb-2">Cypher Battles</h1>
          <p className="text-muted-foreground">Compete, vote, and win. Digital and live battle competitions.</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <Select value={filters.mode} onValueChange={(v) => setFilters({ ...filters, mode: v })}>
            <SelectTrigger className="w-full sm:w-48 bg-muted/50 border-border" data-testid="battle-mode-filter">
              <SelectValue placeholder="Battle Mode" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Modes</SelectItem>
              <SelectItem value="live">Live</SelectItem>
              <SelectItem value="digital">Digital</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="w-full sm:w-48 bg-muted/50 border-border" data-testid="battle-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="voting">Voting</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Battles Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : battles.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="battles-grid">
            {battles.map((battle, index) => (
              <motion.div
                key={battle.battle_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link to={`/battles/${battle.battle_id}`}>
                  <Card className="glass-card border-border overflow-hidden hover-lift h-full">
                    {/* Cover */}
                    <div className="aspect-video bg-muted relative">
                      {battle.cover_url ? (
                        <img 
                          src={battle.cover_url} 
                          alt={battle.title} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                          <Swords className="w-12 h-12 text-muted-foreground" />
                        </div>
                      )}
                      <Badge className={`absolute top-2 right-2 ${getStatusColor(battle.status)}`}>
                        {battle.status?.replace('_', ' ')}
                      </Badge>
                      <Badge className="absolute top-2 left-2 bg-background/50">
                        {battle.battle_mode}
                      </Badge>
                    </div>

                    {/* Content */}
                    <div className="p-4 space-y-3">
                      <h3 className="font-heading text-lg font-bold line-clamp-1">{battle.title}</h3>
                      
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Users className="w-4 h-4" />
                          <span>{battle.participants_count || 0}/{battle.bracket_size}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <DollarSign className="w-4 h-4" />
                          <span>${battle.prize_pool || 0}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Trophy className="w-4 h-4" />
                          <span>{battle.voting_type}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Clock className="w-4 h-4" />
                          <span>{new Date(battle.deadline).toLocaleDateString()}</span>
                        </div>
                      </div>

                      {battle.entry_fee > 0 && (
                        <Badge variant="outline" className="w-full justify-center">
                          Entry: ${battle.entry_fee}
                        </Badge>
                      )}
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        ) : (
          <StageEmptyState
            icon={Swords}
            title="No Battles Yet"
            description="No cypher battles have been created. Stay tuned for upcoming competitions."
          />
        )}
      </div>
    </div>
  );
}
